import { supabase } from '../lib/supabase';

// In-memory cache for conversation IDs keyed by enquiry_id
const conversationCache = new Map();

// Cache key for localStorage
const CACHE_KEY = 'holiyay_conversation_cache';

// Load cache from localStorage on module initialization
const loadCacheFromStorage = () => {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      Object.entries(parsed)?.forEach(([enquiryId, conversationId]) => {
        conversationCache?.set(enquiryId, conversationId);
      });
      console.log('[ConversationCache] Loaded from localStorage:', conversationCache?.size, 'entries');
    }
  } catch (error) {
    console.error('[ConversationCache] Failed to load from localStorage:', error);
  }
};

// Save cache to localStorage
const saveCacheToStorage = () => {
  try {
    const cacheObject = Object.fromEntries(conversationCache);
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error('[ConversationCache] Failed to save to localStorage:', error);
  }
};

// Initialize cache on module load
loadCacheFromStorage();

// Message service with snake_case ↔ camelCase conversion
export const messageService = {
  /**
   * Get or create a conversation for an enquiry
   * Implements deterministic get-or-create flow with caching:
   * 1. Check in-memory cache first
   * 2. Try to find existing conversation by enquiry_id
   * 3. If not found, fetch enquiry details and create conversation + participants
   * 4. Cache and return conversation ID
   */
  async getOrCreateConversationForEnquiry(enquiryId) {
    // Check cache first for instant navigation
    if (conversationCache?.has(enquiryId)) {
      const cachedId = conversationCache?.get(enquiryId);
      console.log('[getOrCreateConversation] Cache hit for enquiry:', enquiryId, '-> conversation:', cachedId);
      return cachedId;
    }

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    console.log('[getOrCreateConversation] Cache miss, querying for enquiry:', enquiryId);

    // Step 1: Try to find existing conversation by enquiry_id (FAST QUERY)
    const { data: existingConversation, error: fetchError } = await supabase
      ?.from('conversations')
      ?.select('id')
      ?.eq('enquiry_id', enquiryId)
      ?.maybeSingle();

    if (fetchError) {
      console.error('[getOrCreateConversation] Error fetching conversation:', fetchError);
      throw new Error(`Failed to fetch conversation: ${fetchError.message}`);
    }

    if (existingConversation) {
      console.log('[getOrCreateConversation] Found existing conversation:', existingConversation?.id);
      // Cache the result
      conversationCache?.set(enquiryId, existingConversation?.id);
      saveCacheToStorage();
      return existingConversation?.id;
    }

    console.log('[getOrCreateConversation] No conversation found, creating new one');

    // Step 2: Fetch enquiry details to get customer_id
    const { data: enquiry, error: enquiryError } = await supabase
      ?.from('enquiries')
      ?.select('id, customer_id, destination, trip_type')
      ?.eq('id', enquiryId)
      ?.single();

    if (enquiryError || !enquiry) {
      console.error('[getOrCreateConversation] Enquiry not found:', enquiryError);
      throw new Error(`Enquiry not found: ${enquiryError?.message || 'Unknown error'}`);
    }

    // Step 2.5: Check if current user has access to this enquiry via agent_enquiry_access
    const { data: accessRecord, error: accessError } = await supabase
      ?.from('agent_enquiry_access')
      ?.select('agent_id')
      ?.eq('enquiry_id', enquiryId)
      ?.eq('agent_id', user?.id)
      ?.maybeSingle();

    if (accessError) {
      console.error('[getOrCreateConversation] Error checking agent access:', accessError);
      throw new Error(`Failed to verify agent access: ${accessError?.message}`);
    }

    if (!accessRecord) {
      console.error('[getOrCreateConversation] Agent does not have access to this enquiry:', { enquiryId, agentId: user?.id });
      throw new Error('You do not have access to this enquiry. Please purchase access first.');
    }

    // Use the current user (agent) ID for the conversation
    const agentId = user?.id;

    if (!enquiry?.customer_id) {
      console.error('[getOrCreateConversation] Enquiry has no customer:', enquiry);
      throw new Error('Enquiry has no customer');
    }

    console.log('[getOrCreateConversation] Enquiry details:', {
      enquiryId: enquiry?.id,
      customerId: enquiry?.customer_id,
      agentId: agentId,
      destination: enquiry?.destination
    });

    // Step 3: Create new conversation
    const conversationTitle = `${enquiry?.destination} - ${enquiry?.trip_type || 'Trip'}`;
    
    const { data: newConversation, error: createError } = await supabase
      ?.from('conversations')
      ?.insert({
        enquiry_id: enquiry?.id,
        customer_id: enquiry?.customer_id,
        agent_id: agentId,
        title: conversationTitle,
        status: 'open'
      })
      ?.select('id')
      ?.single();

    if (createError || !newConversation) {
      console.error('[getOrCreateConversation] Failed to create conversation:', createError);
      throw new Error(`Failed to create conversation: ${createError?.message || 'Unknown error'}`);
    }

    console.log('[getOrCreateConversation] Created conversation:', newConversation?.id);

    // Step 4: Create conversation_participants for customer and agent
    const participants = [
      {
        conversation_id: newConversation?.id,
        user_id: enquiry?.customer_id,
        is_active: true,
        role_in_conversation: 'customer',
        joined_at: new Date()?.toISOString()
      },
      {
        conversation_id: newConversation?.id,
        user_id: agentId,
        is_active: true,
        role_in_conversation: 'agent',
        joined_at: new Date()?.toISOString()
      }
    ];

    const { error: participantsError } = await supabase
      ?.from('conversation_participants')
      ?.insert(participants);

    if (participantsError) {
      console.error('[getOrCreateConversation] Failed to create participants:', participantsError);
      console.warn('[getOrCreateConversation] Conversation created but participants failed. Conversation ID:', newConversation?.id);
    } else {
      console.log('[getOrCreateConversation] Created participants for customer and agent');
    }

    // Cache the new conversation
    conversationCache?.set(enquiryId, newConversation?.id);
    saveCacheToStorage();

    console.log('[getOrCreateConversation] Successfully created conversation:', {
      conversationId: newConversation?.id,
      enquiryId: enquiry?.id,
      customerId: enquiry?.customer_id,
      agentId: enquiry?.agent_id
    });

    return newConversation?.id;
  },

  /**
   * Get all conversations for the current agent
   * Joins with enquiries table to get destination and trip_type
   */
  async getAgentConversations() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('conversations')?.select(`
        id,
        title,
        status,
        last_message_at,
        created_at,
        enquiry_id,
        customer_id,
        enquiries!inner (
          destination,
          trip_type,
          adults,
          children,
          departure_date,
          return_date,
          budget_range,
          description
        )
      `)?.eq('agent_id', user?.id)?.order('last_message_at', { ascending: false, nullsFirst: false })?.order('created_at', { ascending: false });

    if (error) throw error;

    // Fetch customer profiles separately to avoid join issues
    const conversationsWithCustomers = await Promise.all(
      (data || [])?.map(async (row) => {
        let customer = null;
        
        if (row?.customer_id) {
          const { data: customerData } = await supabase
            ?.from('user_profiles')?.select('full_name, email, avatar_url')?.eq('id', row?.customer_id)
            ?.single();
          
          if (customerData) {
            customer = {
              fullName: customerData?.full_name,
              email: customerData?.email,
              avatarUrl: customerData?.avatar_url
            };
          }
        }

        return {
          id: row?.id,
          title: row?.title,
          status: row?.status,
          lastMessageAt: row?.last_message_at,
          createdAt: row?.created_at,
          enquiryId: row?.enquiry_id,
          customerId: row?.customer_id,
          enquiry: row?.enquiries ? {
            destination: row?.enquiries?.destination,
            tripType: row?.enquiries?.trip_type,
            adults: row?.enquiries?.adults,
            children: row?.enquiries?.children,
            departureDate: row?.enquiries?.departure_date,
            returnDate: row?.enquiries?.return_date,
            budgetRange: row?.enquiries?.budget_range,
            description: row?.enquiries?.description
          } : null,
          customer
        };
      })
    );

    return conversationsWithCustomers;
  },

  /**
   * Get a specific conversation by ID with enquiry details
   */
  async getConversationById(conversationId) {
    const { data, error } = await supabase?.from('conversations')?.select(`
        id,
        title,
        status,
        last_message_at,
        created_at,
        enquiry_id,
        customer_id,
        agent_id,
        quote_price,
        quote_currency,
        quote_submitted_at,
        metadata,
        enquiries!inner (
          destination,
          trip_type,
          adults,
          children,
          departure_date,
          return_date,
          preferred_nights,
          budget_range,
          accommodation_preferences,
          description,
          additional_requirements,
          customer_name,
          metadata
        ),
        user_profiles!conversations_customer_id_fkey (
          full_name,
          email,
          avatar_url
        )
      `)?.eq('id', conversationId)?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      title: data?.title,
      status: data?.status,
      lastMessageAt: data?.last_message_at,
      createdAt: data?.created_at,
      enquiryId: data?.enquiry_id,
      customerId: data?.customer_id,
      agentId: data?.agent_id,
      quotePrice: data?.quote_price,
      quoteCurrency: data?.quote_currency,
      quoteSubmittedAt: data?.quote_submitted_at,
      metadata: data?.metadata,
      enquiry: data?.enquiries ? {
        destination: data?.enquiries?.destination,
        tripType: data?.enquiries?.trip_type,
        adults: data?.enquiries?.adults,
        children: data?.enquiries?.children,
        departureDate: data?.enquiries?.departure_date,
        returnDate: data?.enquiries?.return_date,
        preferredNights: data?.enquiries?.preferred_nights,
        budgetRange: data?.enquiries?.budget_range,
        accommodationPreferences: data?.enquiries?.accommodation_preferences,
        description: data?.enquiries?.description,
        additionalRequirements: data?.enquiries?.additional_requirements,
        customerName: data?.enquiries?.customer_name,
        metadata: data?.enquiries?.metadata
      } : null,
      customer: data?.user_profiles ? {
        fullName: data?.user_profiles?.full_name,
        email: data?.user_profiles?.email,
        avatarUrl: data?.user_profiles?.avatar_url
      } : null
    };
  },

  /**
   * Get all messages for a specific conversation
   */
  async getConversationMessages(conversationId) {
    const { data, error } = await supabase?.from('messages')?.select(`
        id,
        content,
        created_at,
        sender_id,
        sender_role,
        is_read,
        message_type,
        file_url,
        file_name,
        user_profiles!messages_sender_id_fkey (
          full_name,
          avatar_url
        )
      `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      content: row?.content,
      createdAt: row?.created_at,
      senderId: row?.sender_id,
      senderRole: row?.sender_role,
      isRead: row?.is_read,
      messageType: row?.message_type,
      fileUrl: row?.file_url,
      fileName: row?.file_name,
      sender: row?.user_profiles ? {
        fullName: row?.user_profiles?.full_name,
        avatarUrl: row?.user_profiles?.avatar_url
      } : null
    })) || [];
  },

  /**
   * Send a new message in a conversation
   */
  async sendMessage(conversationId, content) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Validate conversation exists and user has access
    const { data: conversation, error: convError } = await supabase?.from('conversations')?.select('id, agent_id')?.eq('id', conversationId)?.single();

    if (convError || !conversation) {
      throw new Error('Conversation not found or no access');
    }

    if (conversation?.agent_id !== user?.id) {
      throw new Error('You do not have access to this conversation');
    }

    // Insert message with snake_case for database and get it back immediately
    const { data, error } = await supabase?.from('messages')?.insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content: content,
        message_type: 'text'
      })?.select(`
        id,
        content,
        created_at,
        sender_id,
        sender_role,
        is_read,
        message_type,
        user_profiles!messages_sender_id_fkey (
          full_name,
          avatar_url
        )
      `)?.single();

    if (error) throw error;

    // Convert to camelCase
    return {
      id: data?.id,
      content: data?.content,
      createdAt: data?.created_at,
      senderId: data?.sender_id,
      senderRole: data?.sender_role,
      isRead: data?.is_read,
      messageType: data?.message_type,
      sender: data?.user_profiles ? {
        fullName: data?.user_profiles?.full_name,
        avatarUrl: data?.user_profiles?.avatar_url
      } : null
    };
  },

  /**
   * Subscribe to new messages in a conversation
   */
  subscribeToMessages(conversationId, callback) {
    const channel = supabase?.channel(`messages:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          // Convert snake_case to camelCase
          const message = {
            id: payload?.new?.id,
            content: payload?.new?.content,
            createdAt: payload?.new?.created_at,
            senderId: payload?.new?.sender_id,
            senderRole: payload?.new?.sender_role,
            isRead: payload?.new?.is_read,
            messageType: payload?.new?.message_type
          };
          callback(message);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * CUSTOMER METHODS - Get all enquiries for the current customer
   * Shows which enquiries have responses from agents
   */
  async getCustomerEnquiries() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('enquiries')
      ?.select(`
        id,
        destination,
        trip_type,
        adults,
        children,
        departure_date,
        return_date,
        budget_range,
        status,
        created_at,
        description,
        conversations(
          id,
          agent_id,
          last_message_at,
          messages(count)
        )
      `)
      ?.eq('customer_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      destination: row?.destination,
      tripType: row?.trip_type,
      adults: row?.adults,
      children: row?.children,
      departureDate: row?.departure_date,
      returnDate: row?.return_date,
      budgetRange: row?.budget_range,
      status: row?.status,
      createdAt: row?.created_at,
      description: row?.description,
      responseCount: row?.conversations?.length || 0,
      unreadMessages: row?.conversations?.reduce((sum, conv) => sum + (conv?.messages?.[0]?.count || 0), 0) || 0,
      lastResponseAt: row?.conversations?.sort((a, b) => 
        new Date(b?.last_message_at || 0) - new Date(a?.last_message_at || 0)
      )?.[0]?.last_message_at || null
    })) || [];
  },

  /**
   * CUSTOMER METHODS - Get all agent offers/conversations for a specific enquiry
   */
  async getEnquiryOffers(enquiryId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // First verify the enquiry belongs to the current customer
    const { data: enquiry, error: enquiryError } = await supabase
      ?.from('enquiries')
      ?.select('id, customer_id, destination, trip_type, adults, children, departure_date, return_date, budget_range, description')
      ?.eq('id', enquiryId)
      ?.single();

    if (enquiryError || !enquiry) {
      throw new Error('Enquiry not found');
    }

    if (enquiry?.customer_id !== user?.id) {
      throw new Error('You do not have access to this enquiry');
    }

    // Get all conversations (agent offers) for this enquiry
    const { data, error } = await supabase
      ?.from('conversations')
      ?.select(`
        id,
        title,
        status,
        created_at,
        last_message_at,
        quote_price,
        quote_currency,
        quote_submitted_at,
        agent_id,
        user_profiles!conversations_agent_id_fkey (
          full_name,
          email,
          avatar_url,
          company_name,
          agent_profiles(
            rating,
            total_reviews,
            specializations,
            countries_operated
          )
        ),
        messages(count)
      `)
      ?.eq('enquiry_id', enquiryId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      enquiry: {
        id: enquiry?.id,
        destination: enquiry?.destination,
        tripType: enquiry?.trip_type,
        adults: enquiry?.adults,
        children: enquiry?.children,
        departureDate: enquiry?.departure_date,
        returnDate: enquiry?.return_date,
        budgetRange: enquiry?.budget_range,
        description: enquiry?.description
      },
      offers: data?.map(row => ({
        conversationId: row?.id,
        title: row?.title,
        status: row?.status,
        createdAt: row?.created_at,
        lastMessageAt: row?.last_message_at,
        quotePrice: row?.quote_price,
        quoteCurrency: row?.quote_currency,
        quoteSubmittedAt: row?.quote_submitted_at,
        messageCount: row?.messages?.[0]?.count || 0,
        agent: row?.user_profiles ? {
          id: row?.agent_id,
          fullName: row?.user_profiles?.full_name,
          email: row?.user_profiles?.email,
          avatarUrl: row?.user_profiles?.avatar_url,
          companyName: row?.user_profiles?.company_name,
          rating: row?.user_profiles?.agent_profiles?.[0]?.rating || 0,
          totalReviews: row?.user_profiles?.agent_profiles?.[0]?.total_reviews || 0,
          specializations: row?.user_profiles?.agent_profiles?.[0]?.specializations || [],
          countriesOperated: row?.user_profiles?.agent_profiles?.[0]?.countries_operated || []
        } : null
      })) || []
    };
  },

  /**
   * CUSTOMER METHODS - Send a message as customer in a conversation
   */
  async sendCustomerMessage(conversationId, content) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    console.log('[CUSTOMER MESSAGE SEND] Starting message send:', {
      conversationId,
      currentUserId: user?.id,
      contentLength: content?.length
    });

    // Validate conversation exists and is open
    const { data: conversation, error: convError } = await supabase
      ?.from('conversations')
      ?.select('id, status, customer_id, agent_id')
      ?.eq('id', conversationId)
      ?.single();

    if (convError || !conversation) {
      console.error('[CUSTOMER MESSAGE SEND] Conversation not found:', convError);
      throw new Error('Conversation not found or no access');
    }

    console.log('[CUSTOMER MESSAGE SEND] Conversation details:', {
      conversationId: conversation?.id,
      status: conversation?.status,
      customerId: conversation?.customer_id,
      agentId: conversation?.agent_id
    });

    // Check conversation status
    if (conversation?.status !== 'open') {
      console.error('[CUSTOMER MESSAGE SEND] Conversation not open:', conversation?.status);
      throw new Error(`Cannot send message: conversation is ${conversation?.status}`);
    }

    // Check participant exists with correct permissions
    const { data: participant, error: participantError } = await supabase
      ?.from('conversation_participants')
      ?.select('id, user_id, role_in_conversation, is_active, left_at')
      ?.eq('conversation_id', conversationId)
      ?.eq('user_id', user?.id)
      ?.single();

    console.log('[CUSTOMER MESSAGE SEND] Participant check:', {
      participantFound: !!participant,
      participantData: participant,
      participantError: participantError
    });

    if (participantError || !participant) {
      console.error('[CUSTOMER MESSAGE SEND] Participant not found:', {
        conversationId,
        currentUserId: user?.id,
        error: participantError
      });
      throw new Error('You are not a participant in this conversation');
    }

    // Validate participant permissions
    if (!participant?.is_active) {
      console.error('[CUSTOMER MESSAGE SEND] Participant not active:', {
        conversationId,
        currentUserId: user?.id,
        isActive: participant?.is_active
      });
      throw new Error('Your participation in this conversation is not active');
    }

    if (participant?.left_at) {
      console.error('[CUSTOMER MESSAGE SEND] Participant has left:', {
        conversationId,
        currentUserId: user?.id,
        leftAt: participant?.left_at
      });
      throw new Error('You have left this conversation');
    }

    // Verify user is the customer
    if (conversation?.customer_id !== user?.id) {
      console.error('[CUSTOMER MESSAGE SEND] User is not customer:', {
        conversationId,
        currentUserId: user?.id,
        customerId: conversation?.customer_id
      });
      throw new Error('You do not have access to this conversation');
    }

    console.log('[CUSTOMER MESSAGE SEND] All checks passed, inserting message:', {
      conversationId,
      senderId: user?.id,
      roleInConversation: participant?.role_in_conversation
    });

    // Insert message with snake_case for database
    const { data, error } = await supabase
      ?.from('messages')
      ?.insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content: content,
        message_type: 'text'
      })
      ?.select(`
        id,
        content,
        created_at,
        sender_id,
        sender_role,
        is_read,
        message_type,
        user_profiles!messages_sender_id_fkey (
          full_name,
          avatar_url
        )
      `)
      ?.single();

    if (error) {
      console.error('[CUSTOMER MESSAGE SEND] Insert failed:', {
        conversationId,
        currentUserId: user?.id,
        error
      });
      throw error;
    }

    console.log('[CUSTOMER MESSAGE SEND] Message sent successfully:', {
      messageId: data?.id,
      conversationId,
      senderId: user?.id
    });

    // Convert to camelCase
    return {
      id: data?.id,
      content: data?.content,
      createdAt: data?.created_at,
      senderId: data?.sender_id,
      senderRole: data?.sender_role,
      isRead: data?.is_read,
      messageType: data?.message_type,
      sender: data?.user_profiles ? {
        fullName: data?.user_profiles?.full_name,
        avatarUrl: data?.user_profiles?.avatar_url
      } : null
    };
  }
};

/**
 * Gets conversation by ID with all related data
 */
export const getConversationById = async (conversationId) => {
  try {
    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        enquiry:enquiries!conversations_enquiry_id_fkey (
          id,
          destination,
          trip_type,
          start_date,
          end_date,
          adults,
          children,
          budget_min,
          budget_max,
          accommodation_preference,
          special_requirements,
          status
        ),
        agent:user_profiles!conversations_agent_id_fkey (
          id,
          full_name,
          email,
          phone_number
        ),
        customer:user_profiles!conversations_customer_id_fkey (
          id,
          full_name,
          email,
          phone_number
        )
      `)?.eq('id', conversationId)?.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching conversation:', error);
    return { data: null, error };
  }
};

/**
 * Fetches customer's enquiries with conversation count
 */
export const getCustomerEnquiries = async (customerId) => {
  try {
    const { data, error } = await supabase?.from('enquiries')?.select(`
        *,
        conversations (
          id,
          agent_id,
          last_message_at,
          last_message_preview,
          unread_count_customer
        )
      `)?.eq('customer_id', customerId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching customer enquiries:', error);
    return { data: null, error };
  }
};

/**
 * Fetches conversations/offers for a specific enquiry
 */
export const getEnquiryConversations = async (enquiryId, customerId) => {
  try {
    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        agent:user_profiles!conversations_agent_id_fkey (
          id,
          full_name,
          email,
          phone_number
        ),
        agent_profile:agent_profiles!inner (
          user_id,
          business_address,
          specializations,
          experience_years,
          booking_portal_url,
          stripe_customer_id
        )
      `)?.eq('enquiry_id', enquiryId)?.eq('customer_id', customerId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching enquiry conversations:', error);
    return { data: null, error };
  }
};

/**
 * Gets enquiry details by ID
 */
export const getEnquiryById = async (enquiryId) => {
  try {
    const { data, error } = await supabase?.from('enquiries')?.select('*')?.eq('id', enquiryId)?.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching enquiry:', error);
    return { data: null, error };
  }
};

function getConversationMessages(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getConversationMessages is not implemented yet.', args);
  return null;
}

export { getConversationMessages };
function sendMessage(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: sendMessage is not implemented yet.', args);
  return null;
}

export { sendMessage };
function markMessagesAsRead(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: markMessagesAsRead is not implemented yet.', args);
  return null;
}

export { markMessagesAsRead };
function subscribeToMessages(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: subscribeToMessages is not implemented yet.', args);
  return null;
}

export { subscribeToMessages };
function unsubscribeFromMessages(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: unsubscribeFromMessages is not implemented yet.', args);
  return null;
}

export { unsubscribeFromMessages };

// Export standalone function for direct import
export async function getOrCreateConversationForEnquiry(enquiryId) {
  return messageService?.getOrCreateConversationForEnquiry(enquiryId);
}